import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Select, Space, Statistic, Table, notification } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import adminService from '../../../services/adminService';
import productService from '../../../services/productService';
import { formatItemReportQuantity } from '../../../utils/itemReportQuantity';

const currency = (value) => `£${Number(value || 0).toFixed(2)}`;

const statusTypeMap = {
  success: 'success',
  failed: 'failed',
  canceled: 'canceled'
};

const ItemReportTab = ({
  dateRange,
  selectedCustomerId,
  searchText,
  orderStatusFilter,
  excludeCleaningServices,
  showPricesWithoutVAT,
  selectedItemKeys = [],
  onSelectedItemKeysChange
}) => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [itemOptions, setItemOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const params = useMemo(() => {
    const next = {};
    if (dateRange?.length === 2) {
      next.start_date = dateRange[0];
      next.end_date = dateRange[1];
    }
    if (selectedCustomerId) next.client_id = selectedCustomerId;
    if (searchText) next.search = searchText;
    if (orderStatusFilter && statusTypeMap[orderStatusFilter]) {
      next.status_type = statusTypeMap[orderStatusFilter];
    }
    if (excludeCleaningServices) next.exclude_cleaning_services = 1;
    if (showPricesWithoutVAT) next.prices_without_vat = 1;
    if (selectedItemKeys.length > 0) next.item_keys = selectedItemKeys.join(',');
    return next;
  }, [dateRange, selectedCustomerId, searchText, orderStatusFilter, excludeCleaningServices, showPricesWithoutVAT, selectedItemKeys]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await adminService.getItemsReport(params);
      setRows(response.data || []);
      setTotals(response.totals || {});
    } catch (error) {
      notification.error({
        message: 'Error loading item report',
        description: error.message || 'Unable to load item sales data'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [params]);

  useEffect(() => {
    const fetchItemOptions = async () => {
      setOptionsLoading(true);
      try {
        const response = await productService.getAllProducts({ limit: 1000, status: 1 });
        const products = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const packs = Array.isArray(response?.packs) ? response.packs : [];

        const productOptions = products.map((product) => {
          const title = product.product_title || product.name || `Product #${product.id}`;
          const code = product.product_code ? `${product.product_code} - ` : '';
          return {
            value: `product:${product.id}`,
            label: `${code}${title}`
          };
        });

        const packOptions = packs.map((pack) => ({
          value: `pack:${pack.id}`,
          label: `Pack - ${pack.name || pack.title || `#${pack.id}`}`
        }));

        setItemOptions([...productOptions, ...packOptions]);
      } catch (error) {
        notification.error({
          message: 'Error loading item filters',
          description: error.message || 'Unable to load product and pack filters'
        });
      } finally {
        setOptionsLoading(false);
      }
    };

    fetchItemOptions();
  }, []);

  const columns = [
    {
      title: 'Product Code',
      dataIndex: 'product_code',
      width: 130,
      render: (value) => value || 'N/A'
    },
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      ellipsis: true
    },
    {
      title: 'Quantity Sold',
      dataIndex: 'quantity_sold',
      align: 'right',
      render: formatItemReportQuantity
    },
    {
      title: 'Avg Cost Price',
      dataIndex: 'cost_price',
      align: 'right',
      render: currency
    },
    {
      title: 'Avg Selling Price',
      dataIndex: 'selling_price',
      align: 'right',
      render: currency
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost_price',
      align: 'right',
      render: currency
    },
    {
      title: 'Total Selling',
      dataIndex: 'total_selling_price',
      align: 'right',
      render: currency
    },
    {
      title: 'Profit',
      dataIndex: 'profit',
      align: 'right',
      render: (value) => (
        <span className={Number(value || 0) < 0 ? 'text-red-600' : 'text-green-700'}>
          {currency(value)}
        </span>
      )
    }
  ];

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <Card>
          <Statistic title="Quantity Sold" value={totals.quantity_sold || 0} formatter={formatItemReportQuantity} />
        </Card>
        <Card>
          <Statistic title="Total Cost Price" value={currency(totals.total_cost_price)} />
        </Card>
        <Card>
          <Statistic title="Total Selling Price" value={currency(totals.total_selling_price)} />
        </Card>
        <Card>
          <Statistic title="Profit" value={currency(totals.profit)} />
        </Card>
      </div>

      <Space className="mb-4" wrap>
        <Select
          mode="multiple"
          allowClear
          showSearch
          placeholder="All items"
          value={selectedItemKeys}
          onChange={(value) => onSelectedItemKeysChange?.(value)}
          options={itemOptions}
          loading={optionsLoading}
          optionFilterProp="label"
          maxTagCount="responsive"
          style={{ minWidth: 320 }}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchData}>
          Refresh
        </Button>
      </Space>

      <Table
        rowKey={(record) => record.product_key || record.product_id || record.product_name}
        columns={columns}
        dataSource={rows}
        loading={loading}
        size="middle"
        pagination={{ defaultPageSize: 25, pageSizeOptions: ['25', '50', '100', '200'], showSizeChanger: true }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2}>
                <strong>Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <strong>{formatItemReportQuantity(totals.quantity_sold)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} />
              <Table.Summary.Cell index={4} />
              <Table.Summary.Cell index={5} align="right">
                <strong>{currency(totals.total_cost_price)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                <strong>{currency(totals.total_selling_price)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">
                <strong className={Number(totals.profit || 0) < 0 ? 'text-red-600' : 'text-green-700'}>
                  {currency(totals.profit)}
                </strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default ItemReportTab;
